import { ProcessNames } from '@parallel-fluidics/shared/constants';
import StartRunModalShell from 'features/plc/components/startRunModalShell';
import {
  usePLCValue,
  usePLCValues,
  usePLCWrite,
} from 'features/plc/context';
import fetchLatestProcessLogForPress from 'features/processLog/api/fetchLatestProcessLogForPress';
import ConfigSection from 'pages/PressHMI/components/configSection';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { PressOrientation } from '../../../constants/pressOrientation';
import { usePressHandle, useProgramHandle } from '../hooks/context';
import useCurrentConfig from '../hooks/useCurrentConfig';
import parseTime from './parseTime';

const TEMP_ALLOWED_MAP = Object.freeze({
  forming: {
    pmma: {
      'fbUpperFormingHFTS.fFrontHeaterEngagedSetpoint': { min: 140, max: 160 },
      'fbLowerFormingHFTS.fFrontHeaterEngagedSetpoint': { min: 140, max: 160 },
    },
    cop: {
      'fbUpperFormingHFTS.fFrontHeaterEngagedSetpoint': { min: 170, max: 185 },
      'fbLowerFormingHFTS.fFrontHeaterEngagedSetpoint': { min: 170, max: 185 },
    },
    pc: {
      'fbUpperFormingHFTS.fFrontHeaterEngagedSetpoint': { min: 170, max: 185 },
      'fbLowerFormingHFTS.fFrontHeaterEngagedSetpoint': { min: 170, max: 185 },
    },
  },
  bonding: {
    x1: {
      fCapSideBondingTemp: { min: 105, max: 125 },
      fChipSideBondingTemp: { min: 30, max: 70 },
      fCapSideSuperheatTemp: { min: 105, max: 135 },
      fChipSideSuperheatTemp: { min: 30, max: 70 },
      fCapSideAnnealingTemp: { min: 95, max: 115 },
      fChipSideAnnealingTemp: { min: 95, max: 130 },
      fCapSideFinalTemp: { min: 0, max: 70 },
      fChipSideFinalTemp: { min: 0, max: 70 },
      fCapSideLoadingTemp: { min: 0, max: 70 },
      fChipSideLoadingTemp: { min: 0, max: 70 },
    },
    pc: {
      fCapSideBondingTemp: { min: 140, max: 160 },
      fChipSideBondingTemp: { min: 40, max: 80 },
      fCapSideSuperheatTemp: { min: 160, max: 180 },
      fChipSideSuperheatTemp: { min: 40, max: 80 },
      fCapSideAnnealingTemp: { min: 110, max: 135 },
      fChipSideAnnealingTemp: { min: 130, max: 160 },
      fCapSideFinalTemp: { min: 0, max: 70 },
      fChipSideFinalTemp: { min: 0, max: 70 },
      fCapSideLoadingTemp: { min: 0, max: 70 },
      fChipSideLoadingTemp: { min: 0, max: 70 },
    },
    pmma: {
      fCapSideBondingTemp: { min: 95, max: 110 },
      fChipSideBondingTemp: { min: 30, max: 50 },
      fCapSideSuperheatTemp: { min: 115, max: 135 },
      fChipSideSuperheatTemp: { min: 30, max: 50 },
      fCapSideAnnealingTemp: { min: 65, max: 90 },
      fChipSideAnnealingTemp: { min: 75, max: 100 },
      fCapSideFinalTemp: { min: 0, max: 70 },
      fChipSideFinalTemp: { min: 0, max: 70 },
      fCapSideLoadingTemp: { min: 0, max: 70 },
      fChipSideLoadingTemp: { min: 0, max: 70 },
    },
    coc: {
      fCapSideBondingTemp: { min: 135, max: 155 },
      fChipSideBondingTemp: { min: 30, max: 70 },
      fCapSideSuperheatTemp: { min: 145, max: 170 },
      fChipSideSuperheatTemp: { min: 30, max: 70 },
      fCapSideAnnealingTemp: { min: 95, max: 115 },
      fChipSideAnnealingTemp: { min: 105, max: 130 },
      fCapSideFinalTemp: { min: 0, max: 70 },
      fChipSideFinalTemp: { min: 0, max: 70 },
      fCapSideLoadingTemp: { min: 0, max: 70 },
      fChipSideLoadingTemp: { min: 0, max: 70 },
    },
    'non-standard': {
      fCapSideBondingTemp: { min: 90, max: 160 },
      fChipSideBondingTemp: { min: 30, max: 70 },
      fCapSideSuperheatTemp: { min: 100, max: 180 },
      fChipSideSuperheatTemp: { min: 30, max: 80 },
      fCapSideAnnealingTemp: { min: 65, max: 140 },
      fChipSideAnnealingTemp: { min: 75, max: 160 },
      fCapSideFinalTemp: { min: 0, max: 70 },
      fChipSideFinalTemp: { min: 0, max: 70 },
      fCapSideLoadingTemp: { min: 0, max: 70 },
      fChipSideLoadingTemp: { min: 0, max: 70 },
    },
  },
});

const formatFieldValue = ({ type, value }) => (
  type === 'TIME' ? parseTime(value) : String(value)
);

function StartAutomationModal({
  isOpen,
  close,
  process,
}) {
  const pressHandle = usePressHandle();
  const press = useMemo(() => pressHandle.toLowerCase(), [pressHandle]);
  const programHandle = useProgramHandle();
  const automationEnabledHandle = useMemo(() => `${programHandle}.bProcessAutomationEnabled`, [programHandle]);
  const sessionHandle = useMemo(() => `${programHandle}.sFirebaseSessionID`, [programHandle]);
  const { config, configError } = useCurrentConfig(process);
  const write = usePLCWrite();
  const [modalContext, setModalContext] = useState(null);
  const [previousConfigValues, setPreviousConfigValues] = useState(null);

  const configRows = useMemo(() => config.rows || [], [config.rows]);
  const plcHandles = useMemo(
    () => (config.handles || []).map(({ handle }) => `${programHandle}.${handle}`),
    [config.handles, programHandle],
  );
  const plcValues = usePLCValues(plcHandles);
  const pressOrientation = usePLCValue(`MAIN.${pressHandle}_Bonding_Orientation`);

  const currentConfigValues = useMemo(
    () => configRows.reduce((accumulator, row) => row.values.reduce((nextAccumulator, value) => ({
      ...nextAccumulator,
      [value.handle]: plcValues[`${programHandle}.${value.handle}`],
    }), accumulator), {}),
    [configRows, plcValues, programHandle],
  );

  const getPreviousConfigValues = useCallback((plcConfig) => {
    if (!plcConfig) {
      return null;
    }

    return configRows.reduce((accumulator, row) => row.values.reduce((nextAccumulator, value) => ({
      ...nextAccumulator,
      [value.handle]: plcConfig[value.handle],
    }), accumulator), {});
  }, [configRows]);

  const plcConfig = useMemo(() => ({
    version: config.version,
    ...currentConfigValues,
  }), [config.version, currentConfigValues]);

  const configFields = useMemo(() => configRows.flatMap((row) => (
    row.values.map((field) => ({
      ...field,
      key: field.handle,
      section: row.header,
      group: null,
    }))
  )), [configRows]);

  const getFieldErrorText = useCallback(({
    field,
    currentValue,
    materialValue,
    capMaterialValue,
  }) => {
    const selectedMaterial = process === ProcessNames.FORMING ? materialValue : capMaterialValue;
    const range = TEMP_ALLOWED_MAP[process]?.[selectedMaterial]?.[field.handle];

    if (!range || typeof currentValue !== 'number') {
      return null;
    }

    if (currentValue > range.max) {
      return `Temperature should not exceed ${range.max}`;
    }

    if (currentValue < range.min) {
      return `Temperature should not be below ${range.min}`;
    }

    return null;
  }, [process]);

  const handleContextChange = useCallback(({
    production,
    projectId,
    pPartId,
    stepPPartId,
    materialValue,
    capMaterialValue,
  }) => {
    setModalContext({
      materialValue,
      capMaterialValue,
      pPartId,
      production,
      projectId,
      stepPPartId,
    });
  }, []);

  useEffect(() => {
    if (!modalContext?.production || !modalContext?.projectId) {
      setPreviousConfigValues(null);
      return;
    }
    const fetchProcessLog = async () => {
      const processLog = await fetchLatestProcessLogForPress({
        press,
        project: modalContext.projectId,
        pPart: modalContext.pPartId ?? null,
        process,
        production: true,
      });

      if (!processLog) {
        setPreviousConfigValues(null);
        return;
      }

      setPreviousConfigValues(getPreviousConfigValues(processLog.plcConfig));
    };
    fetchProcessLog().catch(() => {
      setPreviousConfigValues(null);
    });
  }, [
    modalContext?.projectId,
    modalContext?.pPartId,
    modalContext?.production,
    press,
    process,
    getPreviousConfigValues,
  ]);

  const {
    errorTextByKey,
    warningTextByKey,
    warningItems,
  } = useMemo(() => {
    const errorByKey = {};
    const warningByKey = {};
    let hasTempWarning = false;
    let hasMismatchWarning = false;

    configFields.forEach((field) => {
      const currentValue = currentConfigValues[field.key];
      const previousValue = previousConfigValues?.[field.key];

      const errorText = getFieldErrorText({
        field,
        currentValue,
        materialValue: modalContext?.materialValue || null,
        capMaterialValue: modalContext?.capMaterialValue || null,
      });
      errorByKey[field.key] = errorText;
      if (errorText) {
        hasTempWarning = true;
      }

      if (previousValue !== undefined && currentValue !== previousValue) {
        hasMismatchWarning = true;
        warningByKey[field.key] = `Previous value was: ${formatFieldValue({
          type: field.type,
          value: previousValue,
        })}`;
        return;
      }
      warningByKey[field.key] = null;
    });

    const alertItems = [];
    if (hasMismatchWarning) {
      alertItems.push({
        type: 'warning',
        content: 'Some values have changed since the previous production run. Please double-check the configuration below before starting this run.',
        dismissible: false,
        id: 'mismatch_warning',
      });
    }

    if (hasTempWarning) {
      alertItems.push({
        type: 'error',
        content: 'Some temperatures fall outside the expected range for this material. Please investigate.',
        dismissible: false,
        id: 'temp_warning',
      });
    }

    return {
      errorTextByKey: errorByKey,
      warningTextByKey: warningByKey,
      warningItems: alertItems,
    };
  }, [
    configFields,
    currentConfigValues,
    getFieldErrorText,
    modalContext?.capMaterialValue,
    modalContext?.materialValue,
    previousConfigValues,
  ]);

  return (
    <StartRunModalShell
      isOpen={isOpen}
      close={close}
      process={process}
      press={press}
      plcConfig={plcConfig}
      onStarted={async ({ processLogId }) => {
        write({ handle: sessionHandle, value: processLogId });
        write({ handle: automationEnabledHandle, value: true });
      }}
      onContextChange={handleContextChange}
      configSection={(
        <ConfigSection
          configFields={configFields}
          configValues={currentConfigValues}
          errorTextByKey={errorTextByKey}
          warningTextByKey={warningTextByKey}
        />
      )}
      skipAction={async () => {
        write({ handle: sessionHandle, value: ' '.repeat(20) });
        write({ handle: automationEnabledHandle, value: true });
      }}
      configError={configError?.message || null}
      warningItems={warningItems}
      bondingDefaults={{
        pressOrientation: pressOrientation !== undefined ? PressOrientation.toString(pressOrientation) : '',
      }}
    />
  );
}

StartAutomationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  process: PropTypes.oneOf([ProcessNames.FORMING, ProcessNames.BONDING]).isRequired,
};

export default StartAutomationModal;
